import {useDispatch} from 'react-redux'
import {useHistory} from 'react-router-dom'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Tooltip from 'react-bootstrap/Tooltip'
import Button from 'react-bootstrap/Button'
import LocaleDate from '../../LocaleDate/LocaleDate'
import LocaleCurrency from '../../LocaleCurrency/LocaleCurrency'
import {setInReferenceToResource,removeChatResourceReference} from '../messageCenterSlice'
import {useDefaultCurrency} from '../../settings/hooks'
import useCurrentUser from '../../../hooks/useCurrentUser'
import {capitalize,resourceTypeDisplayText} from '../../../util/util'
import EventLink from '../../EventLink/EventLink'

const EventHeader = ({event}) => {
	return (
		<>
			<small><strong>{event.note}</strong></small>
			<small className="ml-auto">
				<LocaleDate 
					date={new Date(`${event.date} 00:00:00`)} 
					includeTime={false}
				/>
			</small>
		</>
	)
}

const SubscriptionHeader = ({subscription})=>{
	return <small><strong>{subscription.name}</strong></small>
}

const PaymentHeader = ({payment})=>{
	return (
		<>
			<small>
				<strong>
					<LocaleDate date={new Date(`${payment.date} 00:00:00`)}/>
				</strong>
			</small>
			<small className="ml-auto">
				<LocaleCurrency amount={payment.amount} ISO={payment.currency_ISO}/>
			</small>
		</>
	)
}

const PackageHeader = ({pkg})=>{
	return (
		<>
			<small>
				<strong>
					{pkg.package_name}
				</strong>
			</small>
			<small className="ml-auto">
				<LocaleCurrency amount={pkg.package_price} ISO="USD"/>
			</small>
		</>
	)
}

const ProductHeader = ({product})=>{
	const defaultCurrency = useDefaultCurrency()
	return (
		<>
			<small>
				<strong>
					{product.title}
				</strong>
			</small>
			<small className="ml-auto">
				<LocaleCurrency amount={product.unit_amount/100} ISO={defaultCurrency.ISO}/>
			</small>
		</>
	)
}

const FormHeader = ({form})=>{
	return (
		<>
			<small>
				<strong>
					{form.form_title}
				</strong>
			</small>
		</>
	)
}

const RecipeHeader = ({recipe}) => {
	return (
		<>
			<small>
				<strong>
					{recipe.name}
				</strong>
			</small>
		</>
	)
}

const ProgramHeader = ({program}) => {
	return (
		<>
			<small>
				<strong>
					{program.programName}
				</strong>
			</small>
		</>
	)
}

const ClassHeader = ({liveClass}) => {
	return (
		<>
			<small>
				<strong>
					{liveClass.title}
				</strong>
			</small>
		</>
	)
}

const ExternalResourceHeader = ({externalResource}) => {
	return (
		<>
			<small>
				<strong>
					{externalResource.title}
				</strong>
			</small>
		</>
	)
}

const SnippetHeader = ({type,resource})=>{
	console.log(type,resource)
	switch(type){
		case "task":
		case "nutrition":
		case "workout": return <EventHeader event={resource}/>
		case "subscription": return <SubscriptionHeader subscription={resource}/>
		case "payment": return <PaymentHeader payment={resource}/>
		case "package": return <PackageHeader pkg={resource}/>
		case "merchandise_item": return <ProductHeader product={resource}/>
		case "custom_form": return <FormHeader form={resource}/>
		case "recipe": return <RecipeHeader recipe={resource}/>
		case "program": return <ProgramHeader program={resource}/>
		case "live_class": return <ClassHeader liveClass={resource}/>
		case "external_resource": return <ExternalResourceHeader externalResource={resource}/>
		default: return <div>Resource Type Not Supported</div>
	}
}

const FinancialsLink = ({text,tab,idParam,id}) => (
	<a 
		href={`/financials_dashboard/?tab=${tab}&${idParam}=${id}`} 
		style={{fontSize:'14px'}}
		className="text-primary"
		target="_blank"
	>
		View {text}
	</a>
)

const RecipeLink = ({id})=>(
	<a 
		href={`/recipeList/?recipe_id=${id}`}
		style={{fontSize:'14px'}} 
		target="_blank"
	>
		View Recipe
	</a>
)

const FormLink = ({id})=>(
	<a 
		href={`/customForms/?form_id=${id}`}
		style={{fontSize:'14px'}} 
		target="_blank"
	>
		View Form
	</a>
)

const ProgramLink = ({id})=>(
	<a 
		href={`/programs/?program_id=${id}`}
		style={{fontSize:'14px'}} 
		target="_blank"
	>
		View Program
	</a>
)

const ClassLink = ({id})=>(
	<a 
		href={`/classes/?class_id=${id}`}
		style={{fontSize:'14px'}} 
		target="_blank"
	>
		View Class
	</a>
)

const ExternalResourceLink = ({id})=>(
	<a 
		href={`/resources/?resource_id=${id}`}
		style={{fontSize:'14px'}} 
		target="_blank"
	>
		View Resource
	</a>
)

const SnippetLink = ({type,resource})=>{
	switch(type){
		//events use same logic
		case "task":
		case "workout":
		case "nutrition": return (
			<EventLink
				eventType={resource.event_type}
				display={resource.event_id}
				client={resource.client_id}
			/>
		)
		case "subscription": return <FinancialsLink text="Subscription" tab="subscriptions" idParam="subscription_id" id={resource.subscription_id}/>
		case "merchandise_item": return <FinancialsLink text="Product" tab="myProducts" idParam="product_id" id={resource.id}/>
		case "package": return <FinancialsLink text="Package" tab="packages" idParam="package_id" id={resource.package_id}/>
		case "payment": return <FinancialsLink text="Payment" tab="payments" idParam="payment_id" id={resource.payment_id}/>
		case "recipe": return <RecipeLink id={resource.id}/>
		case "custom_form": return <FormLink id={resource.form_id}/>
		case "program": return <ProgramLink id={resource.programID}/>
		case "live_class": return <ClassLink id={resource._id}/>
		case "external_resource": return <ExternalResourceLink id={resource.id}/>
		default : return 'Resource Type Not Supported'
	}
}

const RemoveReferenceButton = ({type,id})=>{
	const dispatch = useDispatch()
	const handleRemove = ()=>{
		if(!window.confirm(`Are you sure you want to remove this ${resourceTypeDisplayText(type)} from the chat?`)) return
		dispatch(removeChatResourceReference(id))
	}
	return (
		<OverlayTrigger overlay={
			<Tooltip>Remove this {resourceTypeDisplayText(type)} from the chat</Tooltip>
		}>
			<button
				style={{fontSize:'14px',border:'none',background:'none',color:'#d65689'}}
				onClick={handleRemove} 
			>
				Remove
			</button>
		</OverlayTrigger>
	)
}

const ResourceReference = ({resourceReference})=>{
	const user = useCurrentUser()
	const isClient = user.account_type === 'client'
	const dispatch = useDispatch()

	const handleDiscuss = ()=>{
		dispatch(setInReferenceToResource({
			type:resourceReference.resource_type,
			id:resourceReference.id,
			resource:resourceReference.resource
		}))
	}
	if(!Boolean(resourceReference.resource)||!Boolean(resourceReference.resource_type)) return null
	return (
		<div style={{maxWidth:'400px'}} className="border px-2 py-1 rounded my-2 mt-4 mx-4 position-relative">
			<small 
				style={{
					transform:'translate(0,-100%)',
					position:'absolute',
					left:0,
					top:0,
				}}
				className="d-block text-muted"
			>
				{resourceTypeDisplayText(resourceReference.resource_type)}:
			</small>
			<div className="d-flex flex-wrap">
				<SnippetHeader
					type={resourceReference.resource_type}
					resource={resourceReference.resource}
				/>
			</div>
			<div className="d-flex align-items-center justify-content-between flex-wrap">
				<OverlayTrigger overlay={
					<Tooltip>
						Talk about this {resourceTypeDisplayText(resourceReference.resource_type)} and hide all messages not about this {resourceTypeDisplayText(resourceReference.resource_type)}
					</Tooltip>
				}>
					<button
						style={{
							fontSize:'14px',
							border:'none',
							background:'none',
							padding:'0'
						}}
						onClick={handleDiscuss}
						className="text-info"
					>
						Discuss This
					</button>
				</OverlayTrigger>
				{!isClient&&
					<RemoveReferenceButton
						type={resourceReference.resource_type}
						id={resourceReference.id}
					/>
				}
				<span>
					<SnippetLink
						type={resourceReference.resource_type}
						resource={resourceReference.resource}
					/>
				</span>
			</div>
		</div>
	)
}


export default ResourceReference